/**
 * <pre>
 *    ┌─ Hausnummer: 7
 *    │ ┌─ Stiege: 2
 *    │ │ ┌─ Stock: 5
 *    │ │ │ ┌─ Tür: 1
 * 01#7#2#5#1
 * │  └─────┴─ Hausnummernteile
 * └─ Format: Österreich
 * </pre>
 * -> Im Format "Default" steht nur die Hausnummer zur verfügung
 *
 * Hinweise zur Nutzung:
 * - Zum Bauen einer HouseNo möglichst {@link HouseNo.ofAustriaParts} oder {@link HouseNo.ofDefault} verwenden.
 *   Das macht die Reihenfolge der Teile zur API und nicht Sache des Aufrufers.
 * - Zum Auslesen der typischen Felder gibt es {@link HouseNo.getHouseNumber}, {@link HouseNo.getStiege},
 *   {@link HouseNo.getStock} und {@link HouseNo.getTuer}. Index-Verwechslungen sind damit ausgeschlossen.
 */
export interface HouseNo{
    houseNoVec: string
}
export namespace HouseNo {
    /**
     * Erstellt eine HouseNo anhand eines HouseNoVectors
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function ofHouseNoVec(houseNoVec: string): HouseNo{
        const parsed = parseHouseNo(houseNoVec);
        return ofHouseNoParts(parsed.format, ...parsed.parts);
    }

    /**
     * Erzeugt eine HouseNo aus den Teilen, in der vom Format vorgegebenen Reihenfolge.
     * Bevorzugt {@link ofAustriaParts} / {@link ofDefault} verwenden, um Index-/Reihenfolgenfehler zu vermeiden.
     *
     * @param countryCode  Country-Code (z.B. 'A' für Österreich) oder direkt ein {@link HouseNoFormat}.
     *                     Country-Codes werden case-sensitive geprüft (s. {@link HouseNoFormat.ofCountryCode}),
     *                     unbekannte Codes fallen auf Default zurück.
     * @param houseNoParts Hausnummernteile in der Reihenfolge des Formats — für Österreich also
     *                     `houseNumber, stiege, stock, tuer`. Anzahl muss zum Format passen.
     * @throws HouseNoFormatError Wenn zu viele Hausnummernteile übergeben wurden.
     */
    export function ofHouseNoParts(countryCode: string | HouseNoFormat, ...houseNoParts: string[]): HouseNo{
        const format = typeof countryCode === "string" ? HouseNoFormat.ofCountryCode(countryCode) : countryCode;
        const parts = normalizeParts(houseNoParts, format)

        return {
            houseNoVec: [format.type, ...parts].join("#")
        };
    }

    /**
     * Erzeugt eine Österreich-HouseNo. Die Reihenfolge der Teile ist hier Teil der Signatur — Index-Verwechslungen
     * fallen so beim Compile auf.
     */
    export function ofAustriaParts(houseNumber: string, stiege: string, stock: string, tuer: string): HouseNo{
        return ofHouseNoParts(HouseNoFormat.AUSTRIA, houseNumber, stiege, stock, tuer)
    }

    /**
     * Erzeugt eine Default-HouseNo (alle nicht-österreichischen Länder).
     */
    export function ofDefault(houseNumber: string): HouseNo{
        return ofHouseNoParts(HouseNoFormat.DEFAULT, houseNumber)
    }

    /**
     * Liefert die Teile des Vektors **inklusive Typ-Präfix an Index 0**, symmetrisch zur Java-Methode
     * `HouseNo.getHouseNoParts()`. Für `01#7#2#5#1` also `['01', '7', '2', '5', '1']`.
     *
     * Für direkten Zugriff auf einen einzelnen Teil ist {@link getHouseNoPart} (1-based, ohne Typ) meist sinnvoller.
     *
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function getHouseNoParts(houseNo: string|HouseNo): string[]{
        const parsed = parseHouseNo(houseNo)
        return [parsed.format.type, ...parsed.parts]
    }

    /**
     * @param houseNo
     * @param partIndex one-based, der erste Hausnummernteil hat also Index 1.
     *                  Index 0 (= Typ-Präfix) ist über diese Methode bewusst nicht erreichbar.
     * @return Den Hausnummernteil am gegebenen Index.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     * @throws RangeError Falls `partIndex < 1` oder die übergebene HouseNo keinen entsprechenden Teil hat.
     */
    export function getHouseNoPart(houseNo: string|HouseNo, partIndex: number): string{
        if(partIndex < 1){
            throw new RangeError(`partIndex must be >= 1 (got ${partIndex})`)
        }
        const part = getHouseNoParts(houseNo)[partIndex]
        if(part === undefined){
            throw new RangeError(`no house no part for index "${partIndex}"`)
        }
        return part
    }

    /**
     * Die reine Hausnummer (Index 1) — gilt für alle Formate.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function getHouseNumber(houseNo: string|HouseNo): string{
        return getHouseNoPart(houseNo, 1)
    }

    /**
     * Die Stiege (Treppenhaus) — nur bei Österreich-Format gesetzt, sonst leerer String.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function getStiege(houseNo: string|HouseNo): string{
        return HouseNoFormat.ofHouseNo(houseNo) === HouseNoFormat.AUSTRIA
            ? getHouseNoPart(houseNo, 2)
            : ""
    }

    /**
     * Das Stockwerk — nur bei Österreich-Format gesetzt, sonst leerer String.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function getStock(houseNo: string|HouseNo): string{
        return HouseNoFormat.ofHouseNo(houseNo) === HouseNoFormat.AUSTRIA
            ? getHouseNoPart(houseNo, 3)
            : ""
    }

    /**
     * Die Türnummer — nur bei Österreich-Format gesetzt, sonst leerer String.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function getTuer(houseNo: string|HouseNo): string{
        return HouseNoFormat.ofHouseNo(houseNo) === HouseNoFormat.AUSTRIA
            ? getHouseNoPart(houseNo, 4)
            : ""
    }

    /**
     * Formatiert die gegebene HouseNo in einem menschenlesbaren Format.
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function formatForHuman(houseNo: string|HouseNo): string{
        return parseHouseNo(houseNo)
            .parts
            .filter(part => part.trim() !== '')
            .join(' / ')
    }
}

/**
 * Format eines HouseNoVectors
 */
export interface HouseNoFormat{
    /**
     * Country-Code als Großbuchstabe (z.B. 'A' für Österreich, 'D' für Default).
     * Wird in {@link HouseNoFormat.ofCountryCode} case-sensitive verglichen.
     */
    countryCode: string,
    /**Steht am Anfang des Vektors*/
    type: string,
    /**
     * Die gültigen Hausnummernteile.
     *
     * Enthält auch die maximale Länge des numerischen sowie des alphanumerischen Teils.
     * Das wird hier zwar nicht validiert/verwendet, ist der Vollständigkeit halber aber mit aus dem Java übernommen.
     */
    parts: string[]
}
export namespace HouseNoFormat {
    export const DEFAULT = {countryCode: "D", type: "00", parts: ["A10:6"]};
    export const AUSTRIA = {countryCode: "A", type: "01", parts: ["A10:6", "A10:30", "A10:6", "A10:6"]};

    export const ALL: HouseNoFormat[] = Object.values(HouseNoFormat)
        .filter(value=>typeof value === "object" && "type" in value);

    /**
     * Liefert das HouseNoFormat zum übergebenen Country-Code.
     * Der Vergleich ist **case-sensitive** — passend zum Java-Pendant `HouseNoFormat.ofCountryCode(String)`,
     * das ebenfalls nur exakt `"A"` als Österreich erkennt. So bleibt sichergestellt, dass keine Adresse
     * mit kleinbuchstabigem Country-Code versehentlich als Austria persistiert wird.
     * `trim()` wird vorher angewendet, der erste Buchstabe nach trim entscheidet.
     *
     * @return Das passende HouseNoFormat zum übergebenen Country-Code, ansonsten Default.
     */
    export function ofCountryCode(countryCode: string): HouseNoFormat{
        const firstChar = countryCode.trim().charAt(0);
        return HouseNoFormat.ALL.find(format => format.countryCode === firstChar) ?? HouseNoFormat.DEFAULT;
    }

    /**
     * @return Das Format der übergebenen HouseNo
     * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
     */
    export function ofHouseNo(houseNo: string|HouseNo): HouseNoFormat{
        return parseHouseNo(houseNo).format
    }
}

/**
 * Löst die HouseNo auf. Liefert die Hausnummernteile **ohne** Typ-Präfix; das Format steckt separat im Ergebnis.
 * @throws HouseNoFormatError Falls die übergebene HouseNo ungültig ist.
 */
function parseHouseNo(vec: HouseNo|string){
    const parts = (typeof vec === "string" ? vec : vec.houseNoVec).split("#")
    if (parts.length === 1) {
      return {parts: normalizeParts(parts, HouseNoFormat.DEFAULT), format: HouseNoFormat.DEFAULT};
    }
    const format = ofType(parts.shift())

    return {parts: normalizeParts(parts, format), format}
}

/**
 * Falls zu wenige parts übergeben sind, wird mit leeren parts aufgefüllt, so dass die Anzahl parts zum Format passt.
 * @throws HouseNoFormatError Falls zu viele parts übergeben wurden.
 */
function normalizeParts(parts: string[], format: HouseNoFormat): string[]{
    if(parts.length > format.parts.length){
        throw new HouseNoFormatError('Invalid number of parts for house no vec')
    }

    return [...parts, ...new Array(format.parts.length - parts.length).fill("")];
}

/**
 * @return HouseNoFormat des entsprechenden Typs
 * @throws HouseNoFormatError Falls der type ungültig ist
 */
function ofType(type: string): HouseNoFormat{
    const hnf = HouseNoFormat.ALL
        .find(value=>value.type===type);
    if(!hnf){
        throw new HouseNoFormatError(`invalid house no format type '${type}'`);
    }
    return hnf;
}

export class HouseNoFormatError extends Error {}
